import { GraduationCap, Coffee, Wallet, Users, MapPin, ArrowRight, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";

const benefits = [
  {
    icon: GraduationCap,
    title: "Learning & Growth",
    text: "Continuous learning budgets, certifications, and structured mentorship.",
  },
  {
    icon: Coffee,
    title: "Flexible Work Culture",
    text: "Hybrid and remote-first options with a strong focus on outcomes, not hours.",
  },
  {
    icon: Wallet,
    title: "Competitive Compensation",
    text: "Above-market pay, transparent bands, and performance-linked incentives.",
  },
  {
    icon: Users,
    title: "Collaborative Environment",
    text: "Small senior teams, direct access to leadership, and a real say in what we build.",
  },
];

const roles = [
  {
    role: "Senior Full-Stack Engineer",
    dept: "Custom Development",
    location: "Hyderabad / Remote",
    exp: "5-8 yrs",
  },
  {
    role: "SDET — Quality Engineering",
    dept: "Quality Engineering",
    location: "Hyderabad / Hybrid",
    exp: "3-6 yrs",
  },
  {
    role: "Salesforce Developer",
    dept: "Salesforce and ERP Implementation",
    location: "Remote (India)",
    exp: "3-7 yrs",
  },
  {
    role: "Cloud / DevOps Engineer",
    dept: "Cloud Engineering",
    location: "Bangalore / Remote",
    exp: "4-8 yrs",
  },
];

export default function CareersPage() {
  return (
    <>
      <Seo
        title="Careers — Stantive Soft Solutions"
        description="Join Stantive Soft Solutions — build meaningful software with talented professionals in a growth-focused, collaborative culture."
        path="/careers"
      />
      <PageHero
        eyebrow="Careers"
        image="/images/hero-careers.jpg"
        imagePosition="center"
        title="Join a team where engineers can do meaningful work."
        description="Work alongside talented professionals on cutting-edge technology in an energetic, growth-focused culture that respects your craft and your time."
        actions={
          <>
            <Button
              asChild
              size="lg"
              className="bg-gradient-hero text-primary-foreground shadow-elegant hover:opacity-90"
            >
              <a href="#open-positions">
                View Roles <ArrowRight size={16} className="ml-1" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-ink-foreground/25 bg-ink/25 text-ink-foreground backdrop-blur hover:bg-ink-foreground/10"
            >
              <a href="mailto:careers@stantivesoft.com?subject=Open Application">
                <Mail size={16} className="mr-2" />
                Open Application
              </a>
            </Button>
          </>
        }
        stats={[
          { value: "4", label: "Open Roles" },
          { value: "Remote", label: "Options" },
          { value: "Senior", label: "Teams" },
          { value: "Growth", label: "Culture" },
        ]}
      />

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Why work with us
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              A place engineers actually enjoy
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 80}>
                <div className="h-full rounded-2xl border border-border bg-card p-6 shadow-card">
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
                    <b.icon size={20} />
                  </div>
                  <h3 className="mt-4 font-semibold">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="open-positions" className="scroll-mt-24 bg-secondary/40 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Open positions
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">Currently hiring</h2>
          </Reveal>
          <div className="mt-10 overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <div className="hidden grid-cols-[2fr_1.2fr_1.2fr_0.8fr_auto] gap-4 border-b border-border bg-secondary/60 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground md:grid">
              <div>Role</div>
              <div>Department</div>
              <div>Location</div>
              <div>Experience</div>
              <div />
            </div>
            {roles.map((r, i) => (
              <Reveal key={r.role} delay={i * 60}>
                <div className="grid gap-3 border-b border-border px-6 py-5 last:border-none transition-colors hover:bg-secondary/30 md:grid-cols-[2fr_1.2fr_1.2fr_0.8fr_auto] md:items-center md:gap-4">
                  <div>
                    <div className="font-semibold">{r.role}</div>
                    <div className="md:hidden mt-1 text-xs text-muted-foreground">
                      {r.dept} · {r.location} · {r.exp}
                    </div>
                  </div>
                  <div className="hidden text-sm text-muted-foreground md:block">{r.dept}</div>
                  <div className="hidden text-sm text-muted-foreground md:flex items-center gap-1.5">
                    <MapPin size={14} />
                    {r.location}
                  </div>
                  <div className="hidden text-sm text-muted-foreground md:block">{r.exp}</div>
                  <div>
                    <Button
                      asChild
                      size="sm"
                      className="w-full bg-gradient-hero text-primary-foreground hover:opacity-90 md:w-auto"
                    >
                      <a
                        href={`mailto:careers@stantivesoft.com?subject=Application: ${encodeURIComponent(r.role)}`}
                      >
                        Apply Now <ArrowRight size={14} className="ml-1" />
                      </a>
                    </Button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl bg-gradient-ink p-10 text-center shadow-elegant sm:p-16">
          <div className="relative">
            <h2 className="font-display text-3xl font-bold text-ink-foreground sm:text-4xl">
              Don't see a role that fits?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-ink-muted">
              Send us your resume — we're always keen to hear from great engineers.
            </p>
            <Button
              asChild
              size="lg"
              className="mt-6 bg-gradient-hero text-primary-foreground hover:opacity-90"
            >
              <a href="mailto:careers@stantivesoft.com?subject=Open Application">
                <Mail size={16} className="mr-2" />
                careers@stantivesoft.com
              </a>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
